import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, CreditCard, Users, Shield, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

/**
 * Developer Documentation Page for Gift Contributions Feature Design Decisions
 * 
 * This page documents the various options and decision points for the gift contributions feature,
 * including payment processing, data privacy, UX considerations, and technical implementation.
 * 
 * Access: Controlled by VITE_ENABLE_DEV_DOCS environment variable
 */
export default function GiftContributionsDocumentationPage() {
  const [activeFlow, setActiveFlow] = useState<string | null>(null);

  const flowcharts = [
    {
      id: "contribution-flow",
      title: "Gift Contribution Flow",
      description: "Complete flow from gift selection to payment completion",
      mermaid: `flowchart TD
    A[User Views Gift Registry] --> B{RSVP Required?}
    B -->|No| C[User Clicks Contribute]
    B -->|Yes| D{User Has RSVP?}
    D -->|No| E[Show Message: RSVP Required]
    D -->|Yes| C
    C --> F[Select Contribution Amount]
    F --> G{Amount Type?}
    G -->|Full Amount| H[Pre-fill Full Gift Amount]
    G -->|Partial| I[User Enters Custom Amount]
    G -->|More| J[User Enters Amount > Gift Amount]
    H --> K[Fill Contributor Form]
    I --> K
    J --> K
    K --> L[Name Required]
    K --> M[Email Optional?]
    K --> N[Other Contributors Names]
    K --> O[Note/Message]
    L --> P[Validate Form]
    M --> P
    N --> P
    O --> P
    P --> Q{Validation Passed?}
    Q -->|No| K
    Q -->|Yes| R[Create Charge Record]
    R --> S[Generate Payment Intent with Stripe]
    S --> T[Redirect to Stripe Checkout]
    T --> U{Payment Success?}
    U -->|No| V[Show Error, Return to Form]
    U -->|Yes| W[Update Charge Status]
    W --> X[Send Confirmation Email]
    X --> Y[Update Gift Progress]
    Y --> Z[Show Thank You Message]`,
    },
    {
      id: "data-privacy",
      title: "Data Privacy & GDPR Considerations",
      description: "Privacy and data protection considerations for gift contributions",
      mermaid: `flowchart TD
    A[Gift Contribution Data] --> B[Personal Data Collected]
    B --> C[Name Required]
    B --> D[Email Optional]
    B --> E[Other Contributors Names]
    B --> F[Payment Data via Stripe]
    C --> G[Data Storage]
    D --> G
    E --> G
    F --> H[Stripe Handles PCI Compliance]
    G --> I[Data Retention Policy?]
    I --> J[Right to Deletion]
    I --> K[Data Export]
    I --> L[Consent Management]
    J --> M[GDPR Compliance]
    K --> M
    L --> M
    M --> N[Privacy Policy Link]
    M --> O[Terms of Service]`,
    },
    {
      id: "ux-corner-cases",
      title: "UX Corner Cases & Edge Cases",
      description: "Important edge cases to handle in the user experience",
      mermaid: `flowchart TD
    A[Gift Contribution UX] --> B[Edge Case 1: Over-Contribution]
    A --> C[Edge Case 2: Multiple Contributors]
    A --> D[Edge Case 3: Anonymous Contributions]
    A --> E[Edge Case 4: Duplicate Contributions]
    A --> F[Edge Case 5: Payment Failure]
    B --> B1[Allow > 100%? Show warning?]
    B --> B2[What happens to excess?]
    C --> C1[How to display multiple names?]
    C --> C2[Limit number of contributors?]
    D --> D1[Allow anonymous? Privacy implications?]
    D --> D2[Show as Anonymous or hide?]
    E --> E1[Same email, different amounts?]
    E --> E2[Same person, multiple times?]
    F --> F1[Retry payment?]
    F --> F2[Save draft?]
    F --> F3[Clear form data?]`,
    },
    {
      id: "payment-processing",
      title: "Payment Processing Flow",
      description: "How payments are processed through Stripe",
      mermaid: `flowchart TD
    A[User Submits Contribution Form] --> B[Create Charge Record Pending]
    B --> C[Create Stripe Payment Intent]
    C --> D{Payment Intent Created?}
    D -->|No| E[Log Error, Show Message]
    D -->|Yes| F[Store Payment Intent ID]
    F --> G[Redirect to Stripe Checkout]
    G --> H[User Completes Payment]
    H --> I{Payment Result?}
    I -->|Success| J[Stripe Webhook: payment_intent.succeeded]
    I -->|Failed| K[Stripe Webhook: payment_intent.payment_failed]
    I -->|Canceled| L[User Returns, Payment Pending]
    J --> M[Update Charge Status: Completed]
    M --> N[Update Gift Total Contributions]
    N --> O[Send Confirmation Email]
    K --> P[Update Charge Status: Failed]
    P --> Q[Notify User of Failure]
    L --> R[Charge Remains Pending]
    R --> S[User Can Retry?]`,
    },
    {
      id: "contribution-display",
      title: "Contribution Display & Anonymity",
      description: "How contributions are displayed to event owners and other guests",
      mermaid: `flowchart TD
    A[Contribution Display] --> B{Who Views?}
    B -->|Event Owner| C[See All Details]
    B -->|Public/Guests| D{Anonymous Mode?}
    C --> C1[Full Name]
    C --> C2[Email]
    C --> C3[Amount]
    C --> C4[Note/Message]
    C --> C5[Other Contributors]
    D -->|Yes| E[Show as Anonymous]
    D -->|No| F[Show Name Only]
    E --> E1[Hide Email]
    E --> E2[Show Amount]
    E --> E3[Show Note if allowed]
    F --> F1[Show Name]
    F --> F2[Hide Email]
    F --> F3[Show Amount]
    F --> F4[Show Note if allowed]`,
    },
    {
      id: "decision-tree",
      title: "Gift Contribution Decision Tree",
      description: "Key decisions to make when implementing gift contributions",
      mermaid: `flowchart TD
    A[Start: Gift Contribution Feature] --> B{Require RSVP?}
    B -->|Yes| C[Check RSVP Status]
    B -->|No| D[Allow Anyone to Contribute]
    C --> E{RSVP Found?}
    E -->|No| F[Block Contribution]
    E -->|Yes| D
    D --> G{Require Email?}
    G -->|Yes| H[Email Required]
    G -->|No| I[Email Optional]
    H --> J[Allow Over-Contribution?]
    I --> J
    J -->|Yes| K[Allow > 100% with Warning]
    J -->|No| L[Cap at Remaining Amount]
    K --> M[Support Multiple Contributors?]
    L --> M
    M -->|Yes| N[Collect Other Names]
    M -->|No| O[Single Contributor Only]
    N --> P[Allow Anonymous?]
    O --> P
    P -->|Yes| Q[Add Privacy Toggle]
    P -->|No| R[Always Show Name]
    Q --> S[Implement Payment Flow]
    R --> S
    S --> T[Test & Deploy]`,
    },
  ];

  const currentOptions = [
    {
      feature: "Contribution Form",
      status: "not-implemented",
      details: [
        "❌ Frontend form not implemented",
        "❌ Amount selection (full/partial/more) not implemented",
        "❌ Contributor name field not implemented",
        "❌ Email field (optional) not implemented",
        "❌ Other contributors names field not implemented",
        "❌ Note/message field not implemented",
        "⚠️ Backend ChargeController exists but incomplete (TODO: payment processing)",
      ],
    },
    {
      feature: "Payment Processing",
      status: "partially-implemented",
      details: [
        "✅ Stripe Connect integration exists",
        "✅ PaymentIntent creation capability exists",
        "❌ Payment flow for gift contributions not implemented",
        "❌ Webhook handling for gift contributions not implemented",
        "❌ Charge status updates not implemented",
        "❌ Payment retry logic not implemented",
      ],
    },
    {
      feature: "Data Privacy & GDPR",
      status: "not-implemented",
      details: [
        "❌ Privacy policy link not shown",
        "❌ Consent checkbox not implemented",
        "❌ Data retention policy not defined",
        "❌ Right to deletion not implemented",
        "❌ Data export functionality not implemented",
        "❌ Anonymous contribution option not implemented",
      ],
    },
    {
      feature: "Contribution Display",
      status: "partially-implemented",
      details: [
        "✅ Gift progress calculation exists (totalContributions, remainingAmount)",
        "✅ Progress bar display in ModernChicRegistry",
        "❌ Contribution list/details not displayed",
        "❌ Contributor names not shown",
        "❌ Anonymous mode not implemented",
        "❌ Thank you messages not displayed",
      ],
    },
  ];

  const keyDecisions = [
    {
      category: "Data Privacy & GDPR",
      questions: [
        "Should email be required or optional? (GDPR: less data = better, but email needed for receipts)",
        "How should we handle 'other contributors' names? (Privacy concern: collecting third-party data)",
        "Should we allow anonymous contributions? (Privacy vs. transparency trade-off)",
        "What data retention policy should we implement? (GDPR requires clear policy)",
        "How do we handle right to deletion requests? (GDPR requirement)",
        "Should we show a privacy policy link and consent checkbox? (GDPR best practice)",
      ],
    },
    {
      category: "UX & Edge Cases",
      questions: [
        "Should we allow contributions > 100% of gift amount? (Over-contribution handling)",
        "What happens to excess contributions? (Refund? Keep? Donate to event?)",
        "How many 'other contributors' should we allow? (UX: too many = cluttered form)",
        "Should we prevent duplicate contributions from same email? (Spam prevention vs. legitimate multiple contributions)",
        "What happens if payment fails? (Retry? Save draft? Clear form?)",
        "Should we show a confirmation before redirecting to Stripe? (UX: prevent accidental submissions)",
        "How do we handle partial contributions when gift is already partially funded? (Race condition)",
      ],
    },
    {
      category: "Payment & Technical",
      questions: [
        "Should we create the charge record before or after payment? (Pending vs. completed state)",
        "How do we handle webhook failures? (Idempotency, retry logic)",
        "Should we store payment method details? (PCI compliance: no, use Stripe tokens)",
        "How do we handle currency conversion? (Multi-currency support?)",
        "Should we support payment plans/installments? (Complexity vs. value)",
        "How do we handle refunds? (Manual process? Automated?)",
      ],
    },
    {
      category: "RSVP Integration",
      questions: [
        "Should RSVP be required before contributing? (User's requirement: no, but validate with customers)",
        "If RSVP required, how do we link contributions to RSVP? (Guest ID? Email matching?)",
        "Should we show different UI for RSVP'd vs. non-RSVP'd contributors? (UX differentiation)",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Gift Contributions Documentation</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            This page documents design decisions, options, and implementation status for the gift contributions feature.
            Includes data privacy considerations, UX edge cases, and technical implementation details.
          </p>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This page is only visible when <code className="bg-muted px-1 rounded">VITE_ENABLE_DEV_DOCS=true</code> is set.
              It can be disabled in production by removing this environment variable.
            </AlertDescription>
          </Alert>
        </div>

        {/* Current Implementation Status */}
        <Card>
          <CardHeader>
            <CardTitle>Current Implementation Status</CardTitle>
            <CardDescription>
              What's already implemented and what still needs to be built
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {currentOptions.map((option) => (
                <div key={option.feature} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{option.feature}</h3>
                    <Badge
                      variant={
                        option.status === "implemented"
                          ? "default"
                          : option.status === "partially-implemented"
                          ? "secondary"
                          : "destructive"
                      }
                    >
                      {option.status === "implemented" && (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Implemented
                        </>
                      )}
                      {option.status === "partially-implemented" && (
                        <>
                          <Info className="h-3 w-3 mr-1" />
                          Partially Implemented
                        </>
                      )}
                      {option.status === "not-implemented" && (
                        <>
                          <XCircle className="h-3 w-3 mr-1" />
                          Not Implemented
                        </>
                      )}
                    </Badge>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {option.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Key Decisions & Open Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Key Decisions & Open Questions</CardTitle>
            <CardDescription>
              Important questions that need answers before full implementation, especially around data privacy and UX
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {keyDecisions.map((decision) => (
                <div key={decision.category} className="border-l-4 border-primary pl-4 space-y-2">
                  <h3 className="font-semibold flex items-center gap-2">
                    {decision.category === "Data Privacy & GDPR" && <Shield className="h-4 w-4" />}
                    {decision.category === "UX & Edge Cases" && <AlertTriangle className="h-4 w-4" />}
                    {decision.category === "Payment & Technical" && <CreditCard className="h-4 w-4" />}
                    {decision.category === "RSVP Integration" && <Users className="h-4 w-4" />}
                    {decision.category}
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                    {decision.questions.map((question, idx) => (
                      <li key={idx}>{question}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Flowcharts */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Design Decision Flowcharts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {flowcharts.map((flow) => (
              <Card
                key={flow.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setActiveFlow(activeFlow === flow.id ? null : flow.id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{flow.title}</CardTitle>
                    {activeFlow === flow.id ? (
                      <Badge variant="outline">Expanded</Badge>
                    ) : (
                      <Badge variant="secondary">Click to View</Badge>
                    )}
                  </div>
                  <CardDescription>{flow.description}</CardDescription>
                </CardHeader>
                {activeFlow === flow.id && (
                  <CardContent>
                    <div className="bg-muted rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {flow.mermaid}
                      </pre>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      💡 Copy the Mermaid code above and paste it into{" "}
                      <a
                        href="https://mermaid.live"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Mermaid Live Editor
                      </a>{" "}
                      to visualize this flowchart.
                    </p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Implementation Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Implementation Notes</CardTitle>
            <CardDescription>
              Technical details and considerations for developers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Database Schema</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>charges</code> table: Stores gift contributions (already exists)
                  <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                    <li><code>charge_name</code> - Contributor name (required)</li>
                    <li><code>charge_email</code> - Contributor email (nullable, consider making optional)</li>
                    <li><code>charge_wish</code> - Note/message from contributor (nullable)</li>
                    <li><code>charge_others</code> - Other contributors names (nullable, needs implementation)</li>
                    <li><code>charge_price</code> - Contribution amount</li>
                    <li><code>charge_status</code> - Payment status (false = pending, true = completed)</li>
                    <li><code>charge_external_id</code> - Stripe Payment Intent ID</li>
                  </ul>
                </li>
                <li>
                  <code>gifts</code> table: Gift registry items (already exists)
                  <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                    <li><code>gift_amount</code> - Target amount for the gift</li>
                    <li>Calculated: <code>total_contributions</code> (sum of completed charges)</li>
                    <li>Calculated: <code>remaining_amount</code> (gift_amount - total_contributions)</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">API Endpoints</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>{`GET /api/events/public/{slug}/gifts`}</code> - Get public gifts (already implemented)
                </li>
                <li>
                  <code>{`GET /api/public-gifts?event={uuid}`}</code> - Get public gifts via query (already implemented)
                </li>
                <li>
                  <code>{`POST /api/charges`}</code> - Create contribution (exists but incomplete, needs payment integration)
                </li>
                <li>
                  <code>{`GET /api/gifts/{gift}/contributions`}</code> - Get contributions for a gift (exists, may need public version)
                </li>
                <li>
                  <code>{`POST /api/webhooks/stripe`}</code> - Handle Stripe webhooks (needs implementation for gift contributions)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Frontend Components</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <code>ModernChicRegistry.tsx</code> - Gift registry display (exists, needs contribution button functionality)
                </li>
                <li>
                  <code>GiftContributionForm.tsx</code> - Contribution form (needs to be created)
                </li>
                <li>
                  <code>GiftContributionModal.tsx</code> - Modal/dialog for contribution (needs to be created)
                </li>
                <li>
                  <code>PaymentConfirmation.tsx</code> - Thank you/confirmation page (needs to be created)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Data Privacy Considerations</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <strong>Email Collection:</strong> Consider making email optional to minimize data collection (GDPR principle of data minimization)
                </li>
                <li>
                  <strong>Third-Party Data:</strong> Collecting "other contributors" names means collecting data about people who didn't consent. Consider:
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Limit number of other contributors</li>
                    <li>Add disclaimer about data collection</li>
                    <li>Consider not storing full names, just initials</li>
                  </ul>
                </li>
                <li>
                  <strong>Anonymous Contributions:</strong> Add option to contribute anonymously (privacy vs. transparency trade-off)
                </li>
                <li>
                  <strong>Data Retention:</strong> Define clear policy (e.g., delete after 2 years, or keep indefinitely for accounting)
                </li>
                <li>
                  <strong>Right to Deletion:</strong> Implement functionality to delete contribution data on request (GDPR requirement)
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">UX Edge Cases to Handle</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>
                  <strong>Over-Contribution:</strong> What happens when someone contributes more than the gift amount?
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Option 1: Allow it, show warning, keep excess</li>
                    <li>Option 2: Cap at remaining amount</li>
                    <li>Option 3: Allow it, but mark gift as "over-funded"</li>
                  </ul>
                </li>
                <li>
                  <strong>Race Conditions:</strong> Two people contribute simultaneously, both think gift is available
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Use database transactions</li>
                    <li>Check remaining amount before creating charge</li>
                    <li>Handle gracefully if gift becomes fully funded</li>
                  </ul>
                </li>
                <li>
                  <strong>Payment Failures:</strong> What happens if Stripe payment fails?
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Keep charge record as "pending"</li>
                    <li>Allow retry</li>
                    <li>Clear form or keep data? (UX decision)</li>
                  </ul>
                </li>
                <li>
                  <strong>Duplicate Contributions:</strong> Same person contributes multiple times
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Allow it? (Maybe they want to contribute more)</li>
                    <li>Show warning if same email used recently?</li>
                    <li>Prevent duplicates within X hours?</li>
                  </ul>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

